import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import type { IConversation } from 'src/api/landlord-messages-api';
import type { ILead } from 'src/services/api';
import {
    Auth,
    useAchievements,
    useConversations,
    useLeads,
    useLoggedInUser,
    useNotifications,
    usePollConversations,
    usePollLeads,
    useProperties,
    useTeamSettings,
    useUserSettings,
    isNonLeasedPropertyNotification
} from 'src/services/api';
import { normalizeSource } from 'src/pages/landlord/analytics/utils';
import LandlordLayout from 'src/components/layout/landlord/LandlordLayout';
import { AuthenticatedStateContext } from 'src/authenticated-state/context';
import {usePersistentState} from 'src/services/storage/use-persistent-state';

type Props = {
    children: React.ReactNode;
};

export default function AuthenticatedStateProvider({ children }: Props) {
    const isLoggedIn = !!Auth.accessToken;
    const init = useRef(false);

    const navigate = useNavigate();
    const location = useLocation();
    const [showArchived, setShowArchived] = useState(false);
    const [isInactivePropertiesModalClosed, setIsInactivePropertiesModalClosed] = usePersistentState(
        'inactivePropertiesClosed',
        false
    );

    const { data: loggedInUser } = useLoggedInUser();
    const { data: teamSettings } = useTeamSettings();
    const { data: userSettings } = useUserSettings();
    const { data: achievements } = useAchievements();

    const jwtPayload = Auth.jwtPayload;
    const isBypassPaywall = jwtPayload?.ibp;
    const isPaywallEnabled = !isBypassPaywall && !loggedInUser?.team?.is_active;

    const {
        data: conversations,
        dataUpdatedAt: conversationsUpdatedAt,
        refetch: refetchConversations,
        isLoading: isConversationLoading,
        isFetched: isConversationFetched,
    } = useConversations({
        disabled: !loggedInUser?.is_onboarding_complete || isPaywallEnabled,
        isArchived: showArchived ? undefined : false,
    });

    const {
        data: leads,
        dataUpdatedAt: leadsUpdatedAt,
        refetch: refetchLeads,
        isLoading: isLeadsLoading,
    } = useLeads({
        disabled: !loggedInUser?.is_onboarding_complete || isPaywallEnabled,
        isArchived: showArchived ? true : false,
        includeCoapplicants: true,
    });
    usePollConversations();
    usePollLeads();

    const leadIngestionSources = useMemo(() => {
        if (!leads) { return []; }

        const sources = new Set<string>();
        leads.forEach((lead) => {
            sources.add(normalizeSource(lead.ingestion_source));
        });

        return Array.from(sources).sort();
    }, [leads]);

    const leadsByUuid = useMemo(() => {
        if (!leads) { return new Map(); }

        return leads.reduce((acc, lead) => {
            acc.set(lead.uuid, lead);
            return acc;
        }, new Map<string, ILead>());
    }, [leads]);



    const conversationsByLeadUuid = useMemo(() => {
        if (!conversations) { return new Map(); }

        return conversations.reduce((acc, conversation) => {
            acc.set(conversation.lead_uuid, conversation);
            return acc;
        }, new Map<string, IConversation>());
    }, [conversations]);

    // prefetch properties
    const { data: properties } = useProperties({
        disabled: !loggedInUser?.is_onboarding_complete || isPaywallEnabled,
    });

    const REPLAY_USER_IDS = (import.meta.env.VITE_SENTRY_REPLAY_USER_IDS ?? '').split(',').map(Number);
    const INBOX_PATH = import.meta.env.VITE_SENTRY_REPLAY_PATH;

    useEffect(() => {
        if (!loggedInUser) { return; }

        const isTargetedUser = REPLAY_USER_IDS.includes(loggedInUser.id);
        const isInboxPage = location.pathname.startsWith(INBOX_PATH);

        if (isTargetedUser && isInboxPage) {
            const replay = Sentry.getReplay();
            if (replay && !replay.getReplayId()) {
                replay.start();
            }
        }
    }, [loggedInUser, location.pathname]);

    // Refetch leads and conversations when showArchived changes
    useEffect(() => {
        if (!Auth.accessToken) { return; }

        if (!loggedInUser?.is_onboarding_complete) { return; }

        if (!init.current) {
            init.current = true;
            return;
        }

        refetchLeads();
        refetchConversations();
    }, [loggedInUser, showArchived]);

    const { data: notifications } = useNotifications(!isLoggedIn);

    const isInactivePropertiesModalOpen = useMemo(() => {
        if (isInactivePropertiesModalClosed || !notifications?.length || !properties) {
            return false;
        }

        const validNotifications = notifications
            .filter(notification => !notification.is_read_by_user)
            .filter(isNonLeasedPropertyNotification)
            .filter(notification => {
                const property = properties.find(p => p.id === notification.payload.property_id);
                return !!property;
            });

        return validNotifications.length > 0;
    }, [isInactivePropertiesModalClosed, notifications, properties]);

    if (!Auth.accessToken) {
        navigate('/landlord/login');
        return null;
    }

    if (!loggedInUser || !teamSettings || !userSettings) {
        return (
            <LandlordLayout isLoading />
        );
    }

    if (!loggedInUser?.is_onboarding_complete) {
        return <Navigate to="/landlord/onboard" replace />;
    }

    const areLeadsOrConversationsLoading = !isPaywallEnabled && (!leads || !conversations);
    const isAuthenticatedDataLoading = !loggedInUser || !teamSettings || !notifications || areLeadsOrConversationsLoading || isLeadsLoading || isConversationLoading || !isConversationFetched;
    const isLoading = isLoggedIn && isAuthenticatedDataLoading;
    return (
        <AuthenticatedStateContext.Provider
            value={{
                showArchived,
                setShowArchived,

                loggedInUser,
                teamSettings,
                userSettings,

                leads: leads || [],
                leadsUpdatedAt,
                leadsByUuid,
                leadIngestionSources,

                conversations: conversations || [],
                conversationsUpdatedAt,
                conversationsByLeadUuid,

                notifications: notifications || [],
                achievements: achievements || { achievements: [], user_achievements: [] },

                isPaywallEnabled,

                isInactivePropertiesModalOpen,
                setIsInactivePropertiesModalClosed,

                isLoading,
            }}
        >
            {(isLoading || !loggedInUser) ? (
                <LandlordLayout isLoading />
            ) : (
                children
            )}
        </AuthenticatedStateContext.Provider>
    );
}
